const BASE = '/api';

/**
 * Sitzungsablauf bei Cloudflare Access
 *
 * Access antwortet auf Requests ohne gültige Session mit einem 302 auf die
 * Login-Seite unter <team>.cloudflareaccess.com. Folgt fetch() diesem Redirect,
 * scheitert er an CORS — Firefox meldet "NetworkError when attempting to fetch
 * resource", Chrome "Failed to fetch". Die App zeigte dann "Kein Zugang" mit
 * dieser Meldung, obwohl nur die 24-h-Session abgelaufen war.
 *
 * Mit redirect:'manual' liefert fetch() stattdessen eine opaqueredirect-Antwort.
 * Dann laden wir die Seite neu: Der Dokument-Request läuft durch den Access-
 * Login (E-Mail-Einmalcode) und kommt anschließend auf dieselbe URL zurück.
 */

export class SessionExpiredError extends Error {
  constructor() {
    super('Sitzung abgelaufen — bitte neu anmelden.');
    this.name = 'SessionExpiredError';
  }
}

const RELOAD_GUARD_KEY = 'fwt:access-reload-at';
const RELOAD_GUARD_MS = 30_000;

// Schon der Property-Zugriff auf sessionStorage kann einen SecurityError werfen
// (z.B. blockierte Cookies/Site-Daten), daher erst innerhalb des try auflösen.
const defaultStorage = () => globalThis.sessionStorage;

/**
 * Lädt die Seite neu, damit Cloudflare Access den Login-Flow startet.
 * Höchstens einmal je 30 s, damit eine Fehlkonfiguration keine Reload-Schleife
 * auslöst. Lässt sich der Zeitstempel nicht speichern, wird NICHT neu geladen —
 * sonst gäbe es nach dem Reload keinen Guard und die Schleife wäre endlos. Der
 * Nutzer kommt dann über den Button „Neu anmelden" weiter.
 * Gibt zurück, ob ein Reload angestoßen wurde.
 */
export function reloadForAccessLogin({
  storage = defaultStorage,
  location = globalThis.location,
  now = Date.now(),
} = {}) {
  try {
    const store = typeof storage === 'function' ? storage() : storage;
    const last = Number(store.getItem(RELOAD_GUARD_KEY)) || 0;
    if (now - last < RELOAD_GUARD_MS) return false;
    store.setItem(RELOAD_GUARD_KEY, String(now));
    // Sicherstellen, dass der Guard wirklich persistiert wurde
    if (store.getItem(RELOAD_GUARD_KEY) !== String(now)) return false;
  } catch {
    return false;
  }
  location?.reload();
  return true;
}

export const isAccessRedirect = (res) => res?.type === 'opaqueredirect';

/** Baut apiFetch mit injizierbaren Abhängigkeiten (für Tests). */
export function createApiFetch({
  fetchImpl = (...args) => globalThis.fetch(...args),
  onSessionExpired = reloadForAccessLogin,
} = {}) {
  return async function apiFetch(path, opts = {}) {
    let res;
    try {
      res = await fetchImpl(`${BASE}${path}`, {
        ...opts,
        redirect: 'manual',
        headers: { 'Content-Type': 'application/json', ...(opts.headers ?? {}) },
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      });
    } catch (cause) {
      throw new Error('Server nicht erreichbar — bitte Internetverbindung prüfen.', { cause });
    }

    if (isAccessRedirect(res)) {
      onSessionExpired();
      throw new SessionExpiredError();
    }

    const data = await res.json().catch(() => ({ error: 'Ungültige Antwort' }));
    if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
    return data;
  };
}

const apiFetch = createApiFetch();

export const api = {
  getMe: ()                    => apiFetch('/me'),
  getStudents: ()              => apiFetch('/students'),
  createStudent: (body)        => apiFetch('/students', { method: 'POST', body }),
  getProgress: (id, onlyOpen)  => apiFetch(`/students/${id}/progress${onlyOpen ? '?only_open=1' : ''}`),
  setProgress: (id, body)      => apiFetch(`/students/${id}/progress`, { method: 'POST', body }),
  getDives: (id)               => apiFetch(`/students/${id}/dives`),
  getQuizStats: (id)           => apiFetch(`/students/${id}/quiz-stats`),
  getLernreiheProgress: (id)  => apiFetch(`/students/${id}/lernreihe-progress`),
  addDive: (id, body)          => apiFetch(`/students/${id}/dives`, { method: 'POST', body }),
  invite: (body)               => apiFetch('/invite', { method: 'POST', body }),
  getQuiz: (params)            => apiFetch('/quiz?' + new URLSearchParams(params).toString()),
  submitAttempt: (body)        => apiFetch('/quiz/attempt', { method: 'POST', body }),
  getLernreihen: ()            => apiFetch('/lernreihe'),
  getLernreihe: (id)           => apiFetch(`/lernreihe/${id}`),
  completeStep: (id)           => apiFetch(`/lernreihe/step/${id}/complete`, { method: 'POST', body: {} }),
  // Meldungen
  submitReport: (body)         => apiFetch('/reports', { method: 'POST', body }),
  getReports: (status = 'open') => apiFetch(`/reports?status=${status}`),
  updateReport: (id, body)     => apiFetch(`/reports/${id}`, { method: 'PATCH', body }),
  // Admin
  adminGetUsers: ()            => apiFetch('/admin/users'),
  adminCreateUser: (body)      => apiFetch('/admin/users', { method: 'POST', body }),
  adminUpdateUser: (email, body) => apiFetch(`/admin/users/${encodeURIComponent(email)}`, { method: 'PATCH', body }),
  adminDeleteUser: (email)      => apiFetch(`/admin/users/${encodeURIComponent(email)}`, { method: 'DELETE' }),
};
